import { Attachment, AttachmentFile, notify, useModal } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useCallback, useRef, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { useUserOrganization, useUserOrganizationSettings } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../enums/modalId'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { Origin } from '../../../enums/origin'
import { useIsAutoCegidIntegrationEnabled } from '../../../hooks/useIsAutoCegidIntegrationEnabled'
import { UploadFileHeaders } from '../../../types/uploadFilesHeaders'
import { APIError } from '../../../utils'
import { UploadFilesErrorMessage, UploadFilesErrors } from '../elements/UploadFilesErrorMessage'
import { uploadFile } from '../query-api'

interface UploadProgress {
  current: number
  total: number
}

interface UseUploadFilesOptions extends UploadFileHeaders {
  fileOrigin?: Origin
}

export interface UseUploadFilesProps<TUploadResponse = unknown> {
  onError?: (error: APIError) => void
  onUpload?: (file: AttachmentFile, attachment: Attachment) => Promise<TUploadResponse> | undefined | void
  onSettled?: () => void
  onSuccess?: (data?: (Awaited<TUploadResponse> | undefined)[]) => void
  options?: UseUploadFilesOptions
  skipAutoForwardInfoModal?: boolean
}

interface UseUploadFilesResponse {
  isUploading: boolean
  progress: UploadProgress
  uploadFiles: (filesAccepted: File[], filesRejected?: FileRejection[]) => void
}

const uploadProgressInitialData: UploadProgress = {
  current: 0,
  total: 0,
}

export const useUploadFiles = <TUploadResponse>({
  onError,
  onSettled,
  onSuccess,
  onUpload,
  options = {},
  skipAutoForwardInfoModal = false,
}: UseUploadFilesProps<TUploadResponse>): UseUploadFilesResponse => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>(uploadProgressInitialData)
  const existingFilesErrors = useRef<UploadFilesErrors>()
  const { open: openAttachmentInformCPA } = useModal(ModalId.CpaAttachmentInfoModal)
  const { organizationSettings } = useUserOrganizationSettings()
  const { isAutoCegidIntegrationEnabled } = useIsAutoCegidIntegrationEnabled()

  const { fileOrigin, ...headerOptions } = options

  const handleMultipleFilesMutation = useCallback(
    async (files: File[]) => {
      const resultData: (Awaited<TUploadResponse> | undefined)[] = []
      const totalFilesCount = files.length
      const uploadFilesErrors: UploadFilesErrors = existingFilesErrors.current || {}
      if (!organization?.id) {
        notify({
          id: NotificationKeys.UploadFiles,
          message: t('file.rejected'),
          variant: 'error',
        })
        trackError('"organizationId" has to be provided to create an attachment')

        return
      }

      for (const file of files) {
        setUploadProgress((previousUploadProgress) => ({
          current: previousUploadProgress.current + 1,
          total: totalFilesCount,
        }))

        try {
          const { files: uploadedFiles, attachments: uploadedAttachments } = await uploadFile(file, {
            'x-organizationId': organization.id,
            ...headerOptions,
          })

          const result = await onUpload?.(uploadedFiles?.[0], uploadedAttachments?.[0])
          resultData.push((result ?? uploadedFiles?.[0]) as Awaited<TUploadResponse> | undefined)
        } catch (error: any) {
          const errorMessage = error?.body?.errorMessage
          uploadFilesErrors[file.name] = errorMessage || t('file.rejected.unknown_error')
        }
      }

      const rejectedFilesCount = Object.keys(uploadFilesErrors).length

      if (rejectedFilesCount > 0) {
        notify({
          id: NotificationKeys.UploadFiles,
          message: UploadFilesErrorMessage({ uploadFilesErrors }),
          title: t('file.rejected.count', { count: rejectedFilesCount }),
          variant: rejectedFilesCount === totalFilesCount ? 'error' : 'warning',
        })
      }

      return resultData
    },
    [organization, t, headerOptions, onUpload],
  )

  const { isLoading, mutate: upload } = useMutation(handleMultipleFilesMutation, {
    onError: (error: APIError) => {
      onError?.(error)
    },
    onSettled: () => {
      setUploadProgress(uploadProgressInitialData)
      onSettled?.()
    },
    onSuccess: (data) => {
      if (
        !skipAutoForwardInfoModal &&
        isAutoCegidIntegrationEnabled &&
        organizationSettings?.isCpaAttachmentInfoModalDismissed !== '1'
      ) {
        openAttachmentInformCPA()
      }
      onSuccess?.(data)
    },
  })

  const handleUploadFiles = useCallback(
    (filesAccepted: File[], filesRejected?: FileRejection[]) => {
      existingFilesErrors.current = getExistingUploadErrors(filesRejected)
      upload(filesAccepted)
    },
    [upload],
  )

  return {
    isUploading: isLoading,
    progress: uploadProgress,
    uploadFiles: handleUploadFiles,
  }
}

function getExistingUploadErrors(filesRejected?: FileRejection[]): UploadFilesErrors | undefined {
  return filesRejected?.reduce((result, fileRejected) => {
    const fileName = fileRejected.file.name
    result[fileName] = fileRejected.errors.map((error) => error.message).join(', ')
    return result
  }, {} as UploadFilesErrors)
}
